import { useEffect, useRef, useState } from "react";
import { DownChevronIcon, SendIcon, DownloadIcon } from "../Svg";
import { Button } from "../Button";
import _ from "lodash";
import cx from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { SelectInput } from "../form/SelectInput";
import UIState, { selectMobileLayout } from "../../store/UIState";
import { createSelector } from "reselect";
import { ModalTypes, PredictionStatus } from "../../constants";
import { ShowcaseSettingsPanel } from "./ShowcaseSettingsPanel";
import { AiOndemandApi, PREDICTION_STARTED, PREDICTION_STOPPED, selectApiKeys, selectPredictionId, selectPredictionStatus } from "../../store/models/AiOndemandApi";
import { getOndemandErrorMsg, extractErrorText } from "../../utils";
import { selectCurrentUser } from "../../store/models/User";
import { StepVideo, VoiceChoices } from "../../constants/ondemandApi";
import Loader from "../Loader";
import Tooltip from "../Tooltip";


const VideoItem = ({ item }) => {
  return (
    <div className={cx("ResultBox")}>
      <div className={"ResultBox__Video"}>
        <video className={"ResultBox__Video__Player"} controls>
          <source src={item.video} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className={"ResultBox__Video__DownloadWrapper"}>
          <Tooltip tooltip={"Download"}>
            <a href={item.video} download target={"_blank"}><DownloadIcon /></a>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}

const VideoLoadingItem = ({ loadingData, elapsedTime, predictionStatus }) => {
  const isLoadingPrediction = predictionStatus === PredictionStatus.IN_PROGRESS
    || predictionStatus === PredictionStatus.STARTED;
  if (!loadingData && !isLoadingPrediction) return <></>;

  if (loadingData && loadingData.msg === 'estimation') {
    return (
      <div className={cx("ResultBox")}>
        <div className={"ResultBox__Loading"}>
          <div className={"ResultBox__Loading__TopWrapper"}>
            Scheduled | {elapsedTime}s / {loadingData?.rank_eta?.toFixed(0) ?? '?'}s
          </div>

          <div className={"ResultBox__Loading__CenterWrapper"}>
            <Loader size={"small"} color={"green"} /> Waiting for available slot...
          </div>

          <div className={"ResultBox__Loading__BottomWrapper"}>Number of jobs ahead of you: <span
            style={{ color: '#ffffff' }}>{loadingData.rank + 1}</span></div>
        </div>
      </div>)
  }

  if ((loadingData && loadingData.msg === 'process_starts')
    || isLoadingPrediction) {
    return (<div className={cx("ResultBox")}>

      <div className={"ResultBox__Loading"}>

        <div className={"ResultBox__Loading__TopWrapper"}>
          Processing | {elapsedTime}s / {loadingData?.eta?.toFixed(0) ?? '?'}s
        </div>

        <div className={"ResultBox__Loading__CenterWrapper"}>
          <Loader color={"green"} />
        </div>

        <div className={"ResultBox__Loading__BottomWrapper"}>Video generation in progress...</div>
      </div>
    </div>)
  }
}

const TextItem = ({ message }) => {

  const [paramsOpened, setParamsOpened] = useState(false);

  const onToggleParams = () => {
    setParamsOpened(!paramsOpened);
  }

  return (
    <div className={cx("UserMessageContainer")}>
      <div className="UserMessage">
        <div className={"UserMessage__PromptLabel"}>
          Prompt
        </div>
        <div className={"UserMessage__PromptValue"}>{message.prompt}</div>
        <div className={"UserMessage__PromptLabel"}>
          Negative Prompt
        </div>
        <div className={"UserMessage__PromptValue"}>{message.negative_prompt}</div>
        <div className={"UserMessage__BottomWrapper"}>
          <div className={`UserMessage__ParamsButton ${paramsOpened && "open"}`}
            onClick={onToggleParams}>More parameters <DownChevronIcon /></div>
        </div>
        {paramsOpened &&
          <div className={"UserMessage__Params"}>
            <pre>{JSON.stringify({ prompt: message.text, ...message.params }, null, 2)}</pre>
          </div>}
      </div>

    </div>)
}

const selector = createSelector(
  selectMobileLayout,
  selectApiKeys,
  selectPredictionStatus,
  selectPredictionId,
  selectCurrentUser,
  (isMobile, apiKeys, predictionStatus, predictionId, curUser) => ({
    isMobile,
    apiKeys,
    predictionStatus,
    predictionId,
    curUser
  }
  ))

let intervalId = null;

export const ShowcaseTextToVideo = ({ className, mName = null }) => {
  const dispatch = useDispatch();
  const {
    isMobile,
    apiKeys,
    predictionStatus,
    predictionId,
    curUser
  } = useSelector(state => selector(state));
  const currentModel = StepVideo;
  const apiKey = _.get(apiKeys, '0.key');
  const [promptValue, setPromptValue] = useState('');
  const [negPromptValue, setNegPromptValue] = useState('');
  // const [messages, setMessages] = useState([{
  //   params: {
  //     fps: 25,
  //     frames: 50,
  //     seed: 100,
  //   },
  //   negative_prompt: "low contrast, muddy colors, blurry",
  //   prompt: "A golden retriever puppy playing with a red ball in a sunny park",
  //   type: 'prompt'
  // }, {
  //   type: 'result',
  //   video: "https://beta-ondemand-outputs.thetaedgecloud.com/srvc_8h6vg5ik29yj69286zpbusmh932/prj_ixazr05hnchgfuqyz5azf9q4ncdf/infr_rqst_hh38fu6uns8qf67n69a5vd_video_url.mp4"
  // }]);
  const [messages, setMessages] = useState([]);
  const chatboxRef = useRef(null);
  const [showSettings, setShowSettings] = useState(!isMobile);
  const [settings, setSettings] = useState({});
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    return async () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, []);

  useEffect(() => {
    if (!mName) return;
    if (messages.length > 2) {
      onScrollToPageBottom();
    }
  }, [messages])

  const handleSubmit = async (event) => {
    if (predictionStatus === PredictionStatus.IN_PROGRESS) {
      onStopGenerating(event);
    } else {
      sendMessage(event);
    }
  }

  const onStopGenerating = (event) => {
    event.preventDefault();

    dispatch(UIState.actions.showModal(ModalTypes.CONFIRM, {
      title: "Stop generating",
      message: "Are you sure you want to stop the ongoing generation?",
      onConfirm: onConfirmStopGenerating,
      confirmLabel: "Yes",
      cancelLabel: "No",
      size: 's'
    }))
  }

  const onConfirmStopGenerating = async () => {
    stopInterval();
    try {
      setMessages((messages) => messages.slice(0, -2))
      if (mName) {
        await dispatch(AiOndemandApi.actions.stopHostedPredict(predictionId, apiKey));
        toast.success("Stopped the generation process.")
      }
    } catch (e) {
      toast.success(`Failed to stop the generation. Error: ${e.message}`)
    }
  }


  const startStopwatch = () => {
    if (intervalId) {
      stopInterval()
    }
    const startTime = new Date().getTime()
    intervalId = setInterval(() => {
      const currentTime = new Date().getTime()
      setElapsedTime(((currentTime - startTime) / 1000).toFixed(0))
    }, 1000)

  }

  const stopInterval = () => {
    clearInterval(intervalId)
    intervalId = null
    setElapsedTime(0)
  }

  const sendMessage = async (event) => {
    event.preventDefault();

    if (mName) {
      setPromptValue('');
      setNegPromptValue('');
    }

    const userMsg = {
      prompt: promptValue,
      negative_prompt: negPromptValue,
      params: settings,
      type: 'prompt'
    };
    const botMsg = { type: 'loading' }
    const newMessages = [...messages, userMsg, botMsg];

    setMessages(newMessages)
    const message = {
      prompt: promptValue,
      negative_prompt: negPromptValue,
      ...settings
    };

    generateVideo(message, newMessages.length - 1)

    setTimeout(() => {
      if (!_.isNil(chatboxRef) && !_.isNil(chatboxRef.current)) {
        chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
      }
    }, 1000)
  };

  const generateVideo = async (message, index) => {
    try {
      let video;
      if (mName) {
        startStopwatch();
        let result = await dispatch(AiOndemandApi.actions.startHostedPredict(message, mName, apiKey));
        video = _.get(result, 'body.infer_requests.0.output.video_url')
        stopInterval();
      }
      if (!video) return;
      setMessages(oldMessages => {
        const newMessages = [...oldMessages];
        newMessages[index].video = video;
        newMessages[index].type = 'result';
        return newMessages
      });
    } catch (e) {
      const errMsg = getOndemandErrorMsg(e.message, curUser);
      const toastErr = extractErrorText(errMsg);
      toast.error(toastErr)
      dispatch({ type: PREDICTION_STOPPED })
      stopInterval()
      setMessages(oldMessages => {
        const newMessages = [...oldMessages];
        newMessages[index].type = 'result';
        newMessages[index].error = errMsg;
        return newMessages
      });
    }
  }

  const onScrollToBottom = () => {
    chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
  }

  const onScrollToTop = () => {
    chatboxRef.current.scrollTop = 0;
  }

  const onScrollToPageBottom = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const onToggleSettings = () => {
    setShowSettings(!showSettings);
  }

  const onSettingsChanged = (settings) => {
    setSettings(settings)
  }

  return (
    <div className={cx("ShowcaseTextToVideo", className)}>
      <div className="ShowcaseTextToVideo__container">

        <div className={"ShowcaseTextToVideo__SidePanels"}>
          <div className={"SidePanel"}>
            {!isMobile && <div className={"ShowcaseTextToVideo__LeftInfo"}>
              <div className={"SidePanel__Title"}>Model</div>
              <SelectInput className={"SidePanel__Select"}
                value={{ label: 'StepFun AI / StepVideo', value: 'StepFun AI / StepVideo' }}
                isDisabled={!!mName}
              />
            </div>}

            <div className={"SidePanel__Header"}>
              <div className={"SidePanel__Header__Prompt"}>Prompt</div>
            </div>
            <form className="SidePanel__form" onSubmit={handleSubmit}>
              <textarea name="message"
                className="SidePanel__input"
                value={promptValue}
                onChange={e => setPromptValue(e.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    sendMessage(event);
                  }
                }}
                placeholder="Eg. A time-lapse video of a sunflower field over a sunny day."
                disabled={predictionStatus !== PredictionStatus.STOPPED}
              />
              <div className={"SidePanel__Header"}>
                <div className={"SidePanel__Header__Prompt"}>Negative Prompt</div>
              </div>
              <textarea name="message"
                className="SidePanel__input"
                value={negPromptValue}
                onChange={e => setNegPromptValue(e.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    sendMessage(event);
                  }
                }}
                placeholder="Eg. blurry, low resolution, flicker"
                disabled={predictionStatus !== PredictionStatus.STOPPED}
              />

              {predictionStatus === PredictionStatus.IN_PROGRESS
                ? <Button
                  title={"Stop generating"}
                  type="submit"
                  className="ShowcaseTextToVideo__button"
                  color={"red"} />
                : <Button
                  title={"Send"}
                  type="submit"
                  icon={predictionStatus !== PredictionStatus.STARTED && <SendIcon />}
                  className="ShowcaseTextToVideo__button"
                  color={"green"}
                  size={"small"}
                  loading={predictionStatus === PredictionStatus.STARTED}
                  disabled={_.isEmpty(promptValue) || predictionStatus === PredictionStatus.STARTED} />}
            </form>

            <div className={`SidePanel__OutputOptions ${showSettings && "open"}`}
              onClick={onToggleSettings}>Output options <DownChevronIcon /></div>

            {showSettings && <>
              {isMobile && <div className={"ShowcaseTextToVideo__LeftInfo"}>
                <div className={"SettingsPanel__Title"}>Model</div>
                <SelectInput className={"SidePanel__Select"}
                  value={{ label: 'StepFun AI / StepVideo', value: 'StepFun AI / StepVideo' }}
                  isDisabled={!!mName} />
              </div>}
              <ShowcaseSettingsPanel model={currentModel} onParamsChanged={onSettingsChanged} />
            </>}
          </div>
        </div>

        <div className={cx("ShowcaseTextToVideo__RightSide")}>
          {!_.isEmpty(messages) && <div className={"MessagesWrapper"} ref={chatboxRef}>
            {messages.map((item, index) => {
              switch (item.type) {
                case 'prompt':
                  return (<TextItem key={index} message={item} />)
                case 'loading':
                  return (<VideoLoadingItem key={index} elapsedTime={elapsedTime} predictionStatus={predictionStatus} />)
                case 'result':
                  return (<VideoItem key={index} item={item} />)
              }
            })}
          </div>
          }
        </div>
      </div>
    </div>
  );
}